/**
 * VAVCI · «Visto recientemente» (PDP, WU3 rediseño Concept).
 *
 * localStorage (clave 'vavci:recent') guarda SOLO handles con marca de tiempo —
 * nunca precios: se quedarían obsoletos. En cada PDP se apunta el producto
 * actual (dedupe por handle, el más nuevo primero, tope 12) y se pintan hasta 6
 * de los anteriores EXCLUYENDO el actual, pidiendo /products/{handle}.js para
 * que precio y foto sean los de AHORA. La sección sale del servidor con hidden
 * y solo se muestra si al menos un producto es renderizable.
 *
 * Todo acceso a localStorage va en try/catch (Safari privado, storage lleno…).
 * Las funciones de lista son puras y se testean en Node
 * (scripts/__tests__/pdp-recently-viewed.test.js), mismo patrón que
 * vavci-pdp-variants.js: el DOM queda guardado al final del archivo.
 */
/**
 * Formato de moneda SIN import relativo. vavci-pdp-variants.js (que la sección
 * principal carga con URL versionada `?v=`) publica window.vcMoney en su
 * top-level y se ejecuta antes en el orden del documento. Un
 * `import ... from './vavci-pdp-variants.js'` pediría la URL SIN `?v=` y el CDN
 * de Shopify la sirve de una caché VIEJA (roto en producción el 2026-08-27:
 * «does not provide an export named 'formatEuros'» mataba este módulo entero).
 * La resolución es EN CADA LLAMADA, nunca capturada en el top-level; si el
 * global faltara, un formato mínimo evita el crash.
 * @param {number} cents
 * @returns {string}
 */
export function formatEuros(cents) {
  if (typeof window !== 'undefined' && typeof window.vcMoney === 'function') return window.vcMoney(cents);
  const n = Number(cents);
  return ((Number.isFinite(n) ? n : 0) / 100).toFixed(2).replace('.', ',') + ' €';
}

export const STORAGE_KEY = 'vavci:recent';
export const MAX_STORED = 12;
export const MAX_SHOWN = 6;

/**
 * Añade un handle a la lista: el más nuevo primero, dedupe por handle, tope max.
 * Devuelve una lista NUEVA (no muta) y descarta entradas basura de la guardada.
 * @param {Array<{handle: string, ts: number}>} list
 * @param {string} handle
 * @param {number} ts
 * @param {number} [max]
 * @returns {Array<{handle: string, ts: number}>}
 */
export function pushRecent(list, handle, ts, max = MAX_STORED) {
  const clean = (Array.isArray(list) ? list : []).filter(
    (x) => x && typeof x.handle === 'string' && x.handle !== '' && x.handle !== handle,
  );
  if (typeof handle !== 'string' || handle === '') return clean.slice(0, max);
  return [{ handle, ts: Number(ts) || 0 }, ...clean].slice(0, max);
}

/**
 * Handles a pintar: excluye el producto actual, respeta el orden guardado
 * (más reciente primero) y corta en n.
 * @param {Array<{handle: string, ts: number}>} list
 * @param {string} currentHandle
 * @param {number} n
 * @returns {string[]}
 */
export function pickRecentHandles(list, currentHandle, n = MAX_SHOWN) {
  return (Array.isArray(list) ? list : [])
    .filter((x) => x && typeof x.handle === 'string' && x.handle !== '' && x.handle !== currentHandle)
    .slice(0, n)
    .map((x) => x.handle);
}

/**
 * Miniatura del CDN de Shopify: añade width= respetando la query existente.
 * @param {string} src
 * @param {number} width
 * @returns {string}
 */
export function thumbUrl(src, width) {
  if (typeof src !== 'string' || src === '') return '';
  return src + (src.includes('?') ? '&' : '?') + 'width=' + width;
}

/* ── Enganche con el DOM ──────────────────────────────────────────────────── */

const ROOT = (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function writeStored(list) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (e) { /* privado/lleno: sin memoria, sin drama */ }
}

/** Ficha pública del producto; null si no existe o la red falla. */
async function fetchProduct(handle) {
  try {
    const res = await fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    return null;
  }
}

/**
 * Tarjeta compacta del mock: miniatura, marca, título con elipsis, precio
 * actual y círculo →. Construida con textContent (jamás innerHTML con datos del
 * producto). El precio es el de p.price AHORA (céntimos del endpoint .js).
 */
function buildCard(p) {
  const a = document.createElement('a');
  a.className = 'vrecent__card';
  a.href = p.url || `${ROOT}products/${encodeURIComponent(p.handle)}`;

  const thumb = document.createElement('span');
  thumb.className = 'vrecent__thumb';
  const src = thumbUrl(p.featured_image || (p.images && p.images[0]) || '', 120);
  if (src) {
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.width = 58;
    img.height = 58;
    img.loading = 'lazy';
    img.decoding = 'async';
    thumb.appendChild(img);
  }
  a.appendChild(thumb);

  const body = document.createElement('span');
  body.className = 'vrecent__body';
  if (p.vendor) {
    const brand = document.createElement('span');
    brand.className = 'vrecent__brand';
    brand.textContent = p.vendor;
    body.appendChild(brand);
  }
  const title = document.createElement('span');
  title.className = 'vrecent__name';
  title.textContent = p.title || '';
  body.appendChild(title);
  const price = document.createElement('span');
  price.className = 'vrecent__price';
  price.textContent = formatEuros(p.price);
  body.appendChild(price);
  a.appendChild(body);

  const arrow = document.createElement('span');
  arrow.className = 'vrecent__go';
  arrow.setAttribute('aria-hidden', 'true');
  arrow.textContent = '→';
  a.appendChild(arrow);

  return a;
}

async function init(section) {
  const grid = section.querySelector('[data-vc-recent-grid]');
  const current = section.getAttribute('data-handle') || '';
  if (!grid) return;

  const stored = readStored();
  const handles = pickRecentHandles(stored, current, MAX_SHOWN);
  // El producto actual se apunta DESPUÉS de leer qué pintar: así nunca se lista a sí mismo.
  if (current) writeStored(pushRecent(stored, current, Date.now()));
  if (!handles.length) return;

  const products = (await Promise.all(handles.map(fetchProduct))).filter(Boolean);
  if (!products.length) return;

  for (const p of products) grid.appendChild(buildCard(p));
  section.hidden = false;
}

if (typeof document !== 'undefined') {
  const boot = () => document.querySelectorAll('[data-vc-recent]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}
